import { useDispatch } from "react-redux";
import { generateMultiLevelRouteLink } from "../utils/generateMultiLevelRouteLink";
import {
  joinWithTwoSeparators,
  formatNumber,
  formatTag,
  checkEmptyObject,
  stringifyURI,
  checkEmptyArray
} from "../../../../services/utils";
import {
  AdsSortOption,
  ControlType,
  DataSource,
  isCountryFilterId,
  isSubcategoryDataSource,
  ModuleNameHr,
  QueryParam,
} from "../../../../models/enums";
import { mapCategoryToEn, mapCategoryToHr, mapModuleToHr } from "../../../LanguageMapper";
import {
  isAllowedValue,
  isObject,
  removeProperties,
} from "../../../../services";
import {
  fetchMakes,
  fetchResolvedLocation,
  fetchSubcategories,
  subcategoriesKey
} from "../../../Configuration/configuration.ducks";
import {
  CountryFilterLabels,
  findNodePath,
  makesToNodes,
  resolvedLocationNames
} from "../../../Configuration/helpers";
import { MultiLevelNodeI } from "../../../../typings/configurationTypes";

// Search fields of a multi level control, top level first (includeMakeIds, includeModelIds)
const controlChainFields = (control: any) => {
  const fields = [];
  let child = control && control.child;
  while (child) {
    fields.push(child.field);
    child = child.child;
  }
  return fields;
};

export const useTags = (props, queryParams, state, updateUrlParams) => {
  const dispatch = useDispatch();

  const generateTags = () => {
    const {
      configuration,
      controlFields,
      module,
      modules,
      categories,
      filteredCategories
    } = props;

    if (!configuration || !checkEmptyObject(controlFields)) {
      return;
    }

    const tags = [];
    const selectedControls = [];
    const parsedValues = [];
    let routeLinks = [];

    if (
      module &&
      checkEmptyArray(modules) &&
      modules.find(m => checkEmptyObject(m) && m.route === module)
    ) {
      const moduleName = modules.find(
        m => checkEmptyObject(m) && m.route === module
      ).displayName;

      if (filteredCategories.length > 1) {
        if (module == ModuleNameHr.realEstate) {
          routeLinks = [
            {
              title: moduleName,
              link: `/${module}/${mapCategoryToHr(
                // @ts-ignore
                queryParams.current.category
              )}${stringifyURI(queryParams.current, QueryParam.searchQuery)}`
            }
          ];
        } else {
          routeLinks = [
            {
              title: moduleName,
              link: `/${mapModuleToHr(module)}`
            }
          ];
        }
      }

      // @ts-ignore
      if (queryParams.current.category) {
        if (categories && categories.length > 0) {
          const selectedCategory = categories.find(
            // @ts-ignore
            c => c.route === queryParams.current.category && c.moduleRoute === queryParams.current.module
          );

          if (selectedCategory) {
            routeLinks.push({
              title: selectedCategory.displayName,
              link: `/${mapModuleToHr(module)}/${mapCategoryToHr(
                // @ts-ignore
                queryParams.current.category,
                module
              )}${stringifyURI(queryParams.current, QueryParam.searchQuery)}`
            });
          }
        }
      }
    }

    isObject(queryParams.current) &&
      // eslint-disable-next-line complexity,max-lines-per-function
      Object.keys(queryParams.current).forEach(key => {
        const control = controlFields[key];
        const queryValue = queryParams.current[key];

        if (key === ControlType.searchText && queryValue && isAllowedValue(queryValue)) {
          tags.push({
            id: key,
            field: key,
            type: key,
            name: queryValue
          });
        }

        if (isAllowedValue(queryValue) && control) {
          if (!selectedControls.includes(control.id)) {
            selectedControls.push(control.id);
          }

          switch (control.type) {
            case ControlType.inputLocation:
            case ControlType.multiLevel: {
              const isLocation =
                control.type === ControlType.inputLocation ||
                control.dataSource === DataSource.location;
              const chainFields = controlChainFields(control);

              // dataset of the control: makes of its category or the subcategory
              // tree of its module/category, fetched on first use
              let nodes: MultiLevelNodeI[] | undefined;

              if (control.dataSource === DataSource.make) {
                const makes = configuration.makes[mapCategoryToEn(control.category)];
                if (makes === undefined && control.category) {
                  dispatch(fetchMakes(control.category));
                }
                nodes = makes ? makesToNodes(makes) : undefined;
              } else if (isSubcategoryDataSource(control.dataSource)) {
                nodes = configuration.subcategories[
                  subcategoriesKey(control.module, control.category)
                ];
                if (nodes === undefined && control.module && control.category) {
                  dispatch(fetchSubcategories(control.module, control.category));
                }
              }

              queryValue.forEach(id => {
                const tagId = `${id}_${key}`;

                if (isLocation) {
                  // "Cijela Hrvatska" / "Izvan Hrvatske" are filters, not locations
                  if (isCountryFilterId(id)) {
                    tags.push({
                      id: tagId,
                      field: key,
                      type: control.type,
                      name: formatTag(
                        control.tagFormat,
                        CountryFilterLabels[id].toUpperCase()
                      )
                    });
                    return;
                  }

                  const resolved = configuration.resolvedLocations[id];

                  if (resolved === undefined) {
                    dispatch(fetchResolvedLocation(id));
                    return;
                  }

                  if (resolved) {
                    tags.push({
                      id: tagId,
                      field: key,
                      type: control.type,
                      name: formatTag(
                        control.tagFormat,
                        resolvedLocationNames(resolved).join("・")
                      )
                    });
                  }

                  return;
                }

                const path = nodes ? findNodePath(nodes, id) : null;

                if (!path) {
                  return;
                }

                const parsedValue = path.map((node, index) => ({
                  field: chainFields[index],
                  value: { id: node.id, name: node.name, route: node.route }
                }));

                if (control.child.field !== control.child.excludedField) {
                  parsedValues.push(parsedValue);
                }

                tags.push({
                  id: tagId,
                  field: key,
                  type: control.type,
                  name: formatTag(
                    control.tagFormat,
                    path.map(node => node.name).join("・")
                  )
                });
              });

              break;
            }

            case ControlType.multiFieldSelector: {
              const selectedValue = control.options.find(
                o => o.field === key
              );

              if (selectedValue) {
                tags.push({
                  id: key,
                  name: formatTag(control.tagFormat, selectedValue.title)
                });
              }

              break;
            }

            case ControlType.checkbox:
              tags.push({
                id: key,
                name: formatTag(control.tagFormat, control.title)
              });
              break;

            case ControlType.singleSelector: {
              const selectedValue = control.options.find(
                o => String(o.value) === String(queryValue)
              );

              if (selectedValue) {
                tags.push({
                  id: key,
                  name: formatTag(control.tagFormat, selectedValue.title)
                });
              }

              break;
            }

            case ControlType.multiSelector:
            case ControlType.multiColorSelector: {
              const values = [];
              queryValue.forEach(value => {
                const selectedValue = control.options.find(
                  o => String(o.value) === String(value)
                );

                if (selectedValue) {
                  values.push(selectedValue.title);
                }
              });

              tags.push({
                id: key,
                name: formatTag(
                  control.tagFormat,
                  joinWithTwoSeparators(values)
                )
              });

              break;
            }

            case ControlType.range: {
              let selectedTitle = queryValue;

              if (control.numberFormatted) {
                selectedTitle = formatNumber(parseInt(queryValue, 10));
              }

              if (control.isDateValue) {
                selectedTitle = new Date(queryValue).getUTCFullYear();
              }

              tags.push({
                id: key,
                name: formatTag(control.tagFormat, selectedTitle)
              });
              break;
            }

            default:
              break;
          }
        }
      });

    const generatedRouteLinks = generateMultiLevelRouteLink(parsedValues, routeLinks, module, queryParams);

    state.current = {
      ...state.current,
      tags,
      routeLinks: generatedRouteLinks,
      numberOfControls: selectedControls.length
    };
  };


  const handleTagRemove = tag => {
    const { tags: currentTags } = state.current;

    const tags = currentTags.filter(t => t.id !== tag.id);

    state.current = { ...state.current, tags };

    // remove tag field from url
    switch (tag.type) {
      case ControlType.inputLocation:
      case ControlType.multiLevel:
        queryParams.current[tag.field] = queryParams.current[
          tag.field
        ].filter(f => `${f}_${tag.field}` !== tag.id);

        if (queryParams.current[tag.field].length === 0) {
          queryParams.current = removeProperties(
            queryParams.current,
            tag.field
          );
        }
        break;

      default:
        queryParams.current = removeProperties(queryParams.current, tag.id);
        if (tag.id === 'text') {
          props.updateFilteredCategories([]); // reset filtered categories after search text is removed
        }

        break;
    }

    // Reset page; keep the current sort when there's still text so an AI
    // search's "Najrelevantniji" survives removing an unrelated tag.
    // Otherwise fall back to date-descending — relevancy sort no longer
    // makes sense without text to score against.
    queryParams.current = {
      ...queryParams.current,
      page: undefined,
      sortOption: queryParams.current.text
        ? queryParams.current.sortOption || AdsSortOption.DateDesc
        : AdsSortOption.DateDesc
    };

    //handleSearch();

    updateUrlParams(queryParams.current);
  };


  const handleResetAllTags = () => {
    let currentParams = {};

    // @ts-ignore
    if (queryParams.current.category) {
      currentParams = {
        ...currentParams,
        // @ts-ignore
        category: queryParams.current.category
      };
    }

    // @ts-ignore
    if (queryParams.current.module) {
      currentParams = {
        ...currentParams,
        // @ts-ignore
        module: queryParams.current.module
      };
    }

    // @ts-ignore
    if (queryParams.current.sortOption) {
      currentParams = {
        ...currentParams,
        // @ts-ignore
        sortOption: queryParams.current.sortOption
      };
    }

    // @ts-ignore

    // uncomment this if you dont want to reset text field when reseting all tags
    //if (queryParams.current.text) {
    //  currentParams = {
    //    ...currentParams,
    //    // @ts-ignore
    //    text: queryParams.current.text
    //  };
    //}

    // reset page param
    currentParams = {
      ...currentParams,
      page: undefined,
      // @ts-ignore
      sortOption: AdsSortOption.DateDesc
    };

    queryParams.current = currentParams;

    //handleSearch();

    updateUrlParams(queryParams.current);
  };



  return {
    generateTags,
    handleTagRemove,
    handleResetAllTags
  }
}


// WEBPACK FOOTER //
// ./src/routes/Search/HOCs/hooks/useTags.ts